import { Calendar, Card, Drawer, Statistic, Table } from 'antd';
import React, { Children, useContext, useRef, useState } from 'react';
import { PageContext } from '../../../lib/context';
import GoogleMapReact from 'google-map-react';
import moment from 'moment';
import useSupercluster from 'use-supercluster';
import { CheckOutlined } from '@ant-design/icons';

type Map = {
  latitude: number;
  longitude: number;
  color: string;
  serial_number: string;
  meter_reading: number;
  battery: string;
  reading_date: Date;
  message: string;
  id: number;
};

const Marker = ({ children }: any) => children;
const columns = [
  {
    title: '',
    dataIndex: 'color',
    key: 'color',
    render: (val: any) => (
      <div
        className="w-4 h-4 rounded-full"
        style={{ backgroundColor: val }}
      ></div>
    ),
  },
  {
    title: 'Meter ID',
    key: 'serial_number',
    render: (val: Map) => (
      <a href={`/meters/${val.id}`} target="_blank">
        {val.serial_number}
      </a>
    ),
  },
  {
    title: 'Meter Reading',
    dataIndex: 'meter_reading',
    key: 'meter_reading',
  },
  {
    title: 'Reading Date',
    dataIndex: 'reading_date',
    key: 'reading_date',
    render: (val: any) => moment(val).format('LL'),
  },
  {
    title: 'Info',
    dataIndex: 'message',
    key: 'message',
  },
];

export default function DashboardPage() {
  const {
    map: positions,
    statistics,
    show,
    setShow,
    _showDetails,
    dataSource,
    green,
    black,
    gray,
    orange,
    setGreen,
    setBlack,
    setGray,
    setOrange,
  } = useContext(PageContext);
  const [bounds, setBounds] = useState<number[]>([]);
  const [zoom, setZoom] = useState<number>(10);
  const mapRef: any = useRef();
  const defaultProps = {
    center: {
      lat: 10.58869,
      lng: 124.01552,
    },
    zoom: 11,
  };

  const getMapBounds = (map: any, maps: any) => {
    const bounds = new maps.LatLngBounds();

    positions.forEach(
      ({ latitude, longitude }: { latitude: number; longitude: number }) => {
        bounds.extend(new maps.LatLng(latitude, longitude));
      }
    );
    return bounds;
  };
  const points = positions.map(
    ({
      serial_number,
      latitude,
      longitude,
      color,
      meter_reading,
      reading_date,
      battery,
      message,
      id,
    }: Map) => ({
      type: 'Feature',
      properties: {
        cluster: false,
        serial_number,
        latitude,
        longitude,
        color,
        meter_reading,
        reading_date,
        battery,
        message,
      },
      geometry: {
        type: 'Point',
        coordinates: [longitude, latitude],
      },
    })
  );

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 75, maxZoom: 19 },
  });

  const svgMarker = (maps: any, color: string) => {
    return {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      fillColor: color,
      fillOpacity: 1,
      strokeWeight: 0,
      rotation: 0,
      scale: 1.6,
      anchor: new maps.Point(15, 30),
    };
  };

  const renderMarkers = (map: any, maps: any) => {
    const bounds = getMapBounds(map, maps);
    map.fitBounds(bounds);
    return positions.map(
      ({
        serial_number,
        latitude,
        longitude,
        color,
        meter_reading,
        reading_date,
        battery,
        message,
        id,
      }: Map) => {
        const marker = new maps.Marker({
          position: { lat: latitude, lng: longitude },
          map,
          title: serial_number,
          icon: svgMarker(maps, color),
        });
        const infowindow = new maps.InfoWindow({
          content: `<strong>Meter:</strong> <a href="/meters/${id}" target="_blank">${serial_number}</a><br /><strong>Reading date:</strong> ${
            reading_date ? moment(reading_date).format('LLL') : ''
          }<br/><strong>Reading value:</strong> ${
            meter_reading ?? ''
          }<br/><strong>Battery:</strong> ${
            battery ?? ''
          }<br/><strong>Status: </strong>${message}`,
        });
        marker.addListener('click', function () {
          infowindow.open(map, marker);
        });
        return marker;
      }
    );
  };

  return (
    <div className="flex flex-col px-10">
      <div className="items-start align-start">
        <p className="text-3xl font-bold">Dashboard</p>
      </div>
      <Drawer
        title="Meters"
        placement="right"
        visible={show}
        width="100%"
        onClose={() => setShow(false)}
      >
        <Table dataSource={dataSource} columns={columns} />
      </Drawer>
      <div className="flex flex-row">
        <Card style={{ borderRadius: 20, width: 300, marginRight: 20 }}>
          <Statistic title="Subscribers" value={statistics.subscribers} />
        </Card>
        <Card style={{ borderRadius: 20, width: 300, marginRight: 20 }}>
          <Statistic title="Meters" value={statistics.meters} />
        </Card>
        <Card style={{ borderRadius: 20, width: 300, marginRight: 20 }}>
          <Statistic title="Unassigned Meters" value={statistics.unassigned} />
        </Card>
        <Card style={{ borderRadius: 20, width: 300, marginRight: 20 }}>
          <Statistic title="Assigned Meters" value={statistics.assigned} />
        </Card>
      </div>
      <div className="flex flex-row my-10">
        <div
          className="relative w-full mr-3 bg-gray-400"
          style={{ height: 800 }}
        >
          <div className="w-40 h-30 absolute left-1 top-1 bg-white shadow-lg z-50 rounded">
            <div className="relative p-2 ">
              <div
                className={`h-5 w-5 mb-1 bg-green-600 relative cursor-pointer`}
                onClick={() => setGreen((green: boolean) => !green)}
              >
                {green && (
                  <span
                    className="text-white absolute -top-1 left-0 text-base"
                    style={{ left: 2 }}
                  >
                    <CheckOutlined />
                  </span>
                )}
                <span className="pl-2 w-32 text-xs absolute left-full top-1">
                  Connected
                </span>
              </div>
              <div
                className={`h-5 w-5 mb-1 bg-gray-900 relative cursor-pointer`}
                onClick={() => setBlack((black: boolean) => !black)}
              >
                {black && (
                  <span
                    className="text-white absolute -top-1 left-0 text-base"
                    style={{ left: 2 }}
                  >
                    <CheckOutlined />
                  </span>
                )}
                <span className="pl-2 w-32 text-xs absolute left-full top-1">
                  More than 1 week
                </span>
              </div>
              <div
                className={`h-5 w-5 mb-1 bg-gray-400 relative cursor-pointer`}
                onClick={() => setGray((gray: boolean) => !gray)}
              >
                {gray && (
                  <span
                    className="text-white absolute -top-1 left-0 text-base"
                    style={{ left: 2 }}
                  >
                    <CheckOutlined />
                  </span>
                )}
                <span className="pl-2 w-32 text-xs absolute left-full top-1">
                  More than 2 days
                </span>
              </div>
              <div
                className={`h-5 w-5 mb-1 bg-yellow-600 relative cursor-pointer`}
                onClick={() => setOrange((orange: boolean) => !orange)}
              >
                {orange && (
                  <span
                    className="text-white absolute -top-1 left-0 text-base"
                    style={{ left: 2 }}
                  >
                    <CheckOutlined />
                  </span>
                )}
                <span className="pl-2 w-32 text-xs absolute left-full top-1">
                  Not Reporting
                </span>
              </div>
            </div>
          </div>
          <div className="w-full" style={{ height: 800 }}>
            <GoogleMapReact
              bootstrapURLKeys={{
                key: 'AIzaSyDSQ7BgmenncMg89GCFV20_GBCAJ1K15FU',
              }}
              defaultCenter={defaultProps.center}
              defaultZoom={defaultProps.zoom}
              onChange={({ zoom, bounds }) => {
                setZoom(zoom);
                setBounds([
                  bounds.nw.lng,
                  bounds.se.lat,
                  bounds.se.lng,
                  bounds.nw.lat,
                ]);
              }}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={({ map }) => {
                mapRef.current = map;
                console.log(map);
              }}
              // options={{ mapTypeId: mapRef.current.MapTypeId.SATELLITE }}
              options={map => ({ mapTypeId: map.MapTypeId.HYBRID })}
            >
              {/* {positions.map(
                (
                  {
                    latitude,
                    longitude,
                  }: { latitude: number; longitude: number },
                  index: number
                ) => {
                  if (index === 0) {
                    return (
                      <Marker
                        key={`${index}`}
                        lat={latitude}
                        lng={longitude}
                        text="test"
                      />
                    );
                  }
                }
              )} */}
              {clusters.map((cluster, index) => {
                const [longitude, latitude] = cluster.geometry.coordinates;
                const { cluster: isCluster, point_count: pointCount } =
                  cluster.properties;

                if (isCluster) {
                  return (
                    <Marker
                      key={`cluster-${cluster.id}`}
                      lat={latitude}
                      lng={longitude}
                    >
                      <div
                        className="text-xs rounded-full bg-blue-700 text-white opacity-90 flex justify-center items-center"
                        style={{
                          width: `${20 + (pointCount / points.length) * 20}px`,
                          height: `${20 + (pointCount / points.length) * 20}px`,
                        }}
                        onClick={() => {
                          const expansionZoom = Math.min(
                            supercluster.getClusterExpansionZoom(cluster.id),
                            19
                          );
                          mapRef.current.setZoom(expansionZoom);
                          mapRef.current.panTo({
                            lat: latitude,
                            lng: longitude,
                          });
                        }}
                      >
                        {pointCount}
                      </div>
                    </Marker>
                  );
                }

                return (
                  <Marker key={`${index}`} lat={latitude} lng={longitude}>
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: cluster.properties.color }}
                      onClick={() => _showDetails(longitude, latitude)}
                    ></div>
                  </Marker>
                );
              })}
            </GoogleMapReact>
          </div>
        </div>
      </div>
    </div>
  );
}
